import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export type AppStatus = 'building' | 'finished' | 'queued' | 'hanged'

export interface AppEntry {
  id: string
  name: string
  description: string
  status: AppStatus
  created_at: string
  last_updated: string | null
  model: string | null
  deployment_url: string | null
  source_url: string | null
  started_at: string | null
  finished_at: string | null
  container_name?: string | null
  container_limit?: number
  qa_project_url?: string | null
  secrets_branch?: string | null
  create_pull_requests?: boolean
  repo_url?: string | null
  is_private?: boolean
}

interface AppsState {
  items: AppEntry[]
  total: number
  offset: number
  pageSize: number
  loading: boolean
  error: string | null
  activeFilter: AppStatus
  nameFilter: string
  searching: boolean
  detailLoading: boolean
  detailError: string | null
}

const PAGE_SIZE = 50

const initialState: AppsState = {
  items: [],
  total: 0,
  offset: 0,
  pageSize: PAGE_SIZE,
  loading: false,
  error: null,
  activeFilter: 'building',
  nameFilter: '',
  searching: false,
  detailLoading: false,
  detailError: null,
}

export const fetchApps = createAsyncThunk(
  'apps/fetchApps',
  async ({ status, offset, limit }: { status: string; offset: number; limit: number }) => {
    const params = new URLSearchParams({ status, offset: String(offset), limit: String(limit) })
    const response = await fetch(`/api/apps?${params}`)
    if (!response.ok) throw new Error('Failed to fetch apps')
    return response.json() as Promise<{ apps: AppEntry[]; total: number }>
  },
)

export const searchAppsByName = createAsyncThunk(
  'apps/searchAppsByName',
  async ({ query, status }: { query: string; status: string }) => {
    const params = new URLSearchParams({ search: query, status })
    const response = await fetch(`/api/apps?${params}`)
    if (!response.ok) throw new Error('Failed to search apps')
    return response.json() as Promise<{ apps: AppEntry[]; total: number }>
  },
)

export const fetchAppById = createAsyncThunk('apps/fetchAppById', async (id: string) => {
  const response = await fetch(`/api/apps/${id}`)
  if (!response.ok) {
    // 404 also covers private apps hidden from the current (or anonymous) viewer.
    throw new Error(
      response.status === 404
        ? 'This app could not be found. It may not exist, or it may be private and only visible to its owners.'
        : 'Failed to load this app. Please try again.',
    )
  }
  return response.json() as Promise<AppEntry>
})

export const deleteApp = createAsyncThunk(
  'apps/deleteApp',
  async (appId: string) => {
    const response = await fetch('/api/delete-app', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ appId }),
    })
    if (!response.ok) {
      const data = await response.json()
      throw new Error(data.error || 'Failed to delete app')
    }
    return { appId }
  }
)

export const updateApp = createAsyncThunk(
  'apps/updateApp',
  async (params: {
    appId: string
    name?: string
    description?: string
    deployment_url?: string | null
    source_url?: string | null
    secrets_branch?: string | null
    create_pull_requests?: boolean
    is_private?: boolean
  }) => {
    const { appId, ...fields } = params
    const response = await fetch(`/api/apps/${appId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    })
    if (!response.ok) {
      const data = await response.json()
      throw new Error(data.error || 'Failed to update app')
    }
    return { appId, ...fields }
  }
)

export const copyApp = createAsyncThunk(
  'apps/copyApp',
  async (params: { sourceAppId: string; deployUrl: string; instructions: string }) => {
    const response = await fetch('/api/copy-app', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    })
    if (!response.ok) {
      const data = await response.json()
      throw new Error(data.error || 'Failed to copy app')
    }
    return response.json() as Promise<{ appId: string; status: string; sourceAppId: string }>
  }
)

const appsSlice = createSlice({
  name: 'apps',
  initialState,
  reducers: {
    setActiveFilter(state, action: { payload: AppStatus }) {
      state.activeFilter = action.payload
      state.offset = 0
      state.nameFilter = ''
      state.searching = false
    },
    setNameFilter(state, action: { payload: string }) {
      state.nameFilter = action.payload
      state.searching = action.payload.length > 0
      if (!action.payload) {
        state.offset = 0
      }
    },
    setOffset(state, action: { payload: number }) {
      state.offset = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchApps.pending, (state) => {
        if (state.items.length === 0) {
          state.loading = true
        }
        state.error = null
      })
      .addCase(fetchApps.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload.apps
        state.total = action.payload.total
      })
      .addCase(fetchApps.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch apps'
      })
      .addCase(searchAppsByName.pending, (state) => {
        state.error = null
      })
      .addCase(searchAppsByName.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload.apps
        state.total = action.payload.total
      })
      .addCase(searchAppsByName.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to search apps'
      })
      .addCase(deleteApp.fulfilled, (state, action) => {
        state.items = state.items.filter((a) => a.id !== action.payload.appId)
        state.total = Math.max(0, state.total - 1)
      })
      .addCase(fetchAppById.pending, (state) => {
        state.detailLoading = true
        state.detailError = null
      })
      .addCase(fetchAppById.fulfilled, (state, action) => {
        state.detailLoading = false
        state.detailError = null
        const index = state.items.findIndex((a) => a.id === action.payload.id)
        if (index >= 0) {
          state.items[index] = action.payload
        } else {
          state.items.push(action.payload)
        }
      })
      .addCase(fetchAppById.rejected, (state, action) => {
        state.detailLoading = false
        state.detailError = action.error.message || 'Failed to load this app.'
      })
      .addCase(updateApp.fulfilled, (state, action) => {
        const { appId, name, description, deployment_url, source_url, secrets_branch, create_pull_requests, is_private } = action.payload
        const app = state.items.find((a) => a.id === appId)
        if (app) {
          if (name !== undefined) app.name = name
          if (description !== undefined) app.description = description
          if (deployment_url !== undefined) app.deployment_url = deployment_url || null
          if (source_url !== undefined) app.source_url = source_url || null
          if (secrets_branch !== undefined) app.secrets_branch = secrets_branch || null
          if (create_pull_requests !== undefined) app.create_pull_requests = create_pull_requests
          if (is_private !== undefined) app.is_private = is_private
        }
      })
  },
})

export const { setActiveFilter, setNameFilter, setOffset } = appsSlice.actions
export default appsSlice.reducer
